/**
 * Registro Central de Fuentes Oficiales para Geocodificación PTEL
 * 
 * Este módulo mapea cada tipología PTEL a su fuente de datos oficial española,
 * implementando la arquitectura "Fuentes Oficiales Primero" (v13.0).
 * 
 * Las fuentes oficiales ofrecen:
 * - Cobertura 90-100% de infraestructuras andaluzas
 * - Coordenadas pre-geocodificadas con precisión ±2-25m
 * - Datos validados por autoridades competentes
 * - Actualización periódica garantizada
 * 
 * Fuentes principales:
 * - DERA (Datos Espaciales de Referencia de Andalucía) - IECA
 * - API CKAN Junta de Andalucía (Educación, Datos Abiertos)
 * - IAPH (Instituto Andaluz del Patrimonio Histórico)
 * - REDIAM (Red de Información Ambiental de Andalucía)
 * - Agencia Andaluza de la Energía
 * - IAID (Instituto Andaluz del Deporte)
 * - ISE (Infraestructura de Seguridad y Emergencias)
 * 
 * @module services/geocoding/OfficialSourceRegistry
 * @version 1.0.0
 * @see https://www.ideandalucia.es/services - Catálogo DERA
 * @see https://www.juntadeandalucia.es/datosabiertos - Portal Datos Abiertos
 */

import { TipologiaPTEL as TipologiaPTELMacro } from '@/types/tipologia';

// =============================================================================
// TIPOS Y CONSTANTES
// =============================================================================

/**
 * Códigos de fuente oficial para infraestructuras PTEL.
 *
 * Identificadores de 3 letras que distinguen el origen de los datos
 * geocodificados (fuentes españolas oficiales con cobertura territorial
 * Andalucía: DERA, CKAN, IAPH, REDIAM, AAE, IAID, ISE).
 *
 * Renombrado en TUN3 (7-may-2026) desde el antiguo `type TipologiaPTEL`
 * (15 valores 3-letras) para eliminar el conflicto de nombre con el
 * canónico macro-19 `TipologiaPTEL` (`src/types/tipologia.ts`).
 *
 * Adaptador `TipologiaPTEL` macro → `CodigoFuente`: ver `tipologiaACodigoFuente`
 * más abajo en este mismo módulo.
 *
 * @see .ptel/DECISION_TIPOLOGIA_UNIFY_20260507_TUN1.md §6.1
 */
export enum CodigoFuente {
  SAN = 'SAN',  // Sanitario: hospitales, centros de salud, consultorios
  EDU = 'EDU',  // Educativo: colegios, institutos, guarderías
  CUL = 'CUL',  // Cultural: museos, bibliotecas, teatros, patrimonio
  DEP = 'DEP',  // Deportivo: polideportivos, piscinas, campos de fútbol
  EME = 'EME',  // Emergencias: bomberos, protección civil, 112
  IND = 'IND',  // Industrial: fábricas, polígonos industriales
  TRA = 'TRA',  // Transporte: estaciones, aeropuertos, puertos
  ENE = 'ENE',  // Energía: subestaciones, centrales, parques eólicos
  HID = 'HID',  // Hidráulico: EDAR, embalses, depósitos agua
  ADM = 'ADM',  // Administrativo: ayuntamientos, juzgados, oficinas públicas
  REL = 'REL',  // Religioso: iglesias, ermitas, cementerios
  COM = 'COM',  // Comercial: mercados, grandes superficies, gasolineras
  ACO = 'ACO',  // Acogida: residencias, albergues, centros sociales
  SEG = 'SEG',  // Seguridad: comisarías, cuarteles Guardia Civil
  TEL = 'TEL',  // Telecomunicaciones: antenas, repetidores
}

/**
 * Array de los 15 códigos de fuente en orden canónico.
 * Análogo a `TIPOLOGIAS_VALUES` de macro-19.
 */
export const CODIGOS_FUENTE_VALUES: readonly CodigoFuente[] = Object.freeze(
  Object.values(CodigoFuente),
);

/**
 * Mapeo `CodigoFuente` → `InfrastructureType` macro (string compatible con
 * `TipologiaPTEL`). Inversa parcial: 4 cat macro merge a códigos compartidos
 * (BOMBEROS+EMERGENCIAS→EME, VIAL+PUERTO→TRA, RSU/GENERICO sin código).
 *
 * @see .ptel/DECISION_TIPOLOGIA_UNIFY_20260507_TUN1.md §6.1
 */
export const TIPOLOGIA_TO_INFRASTRUCTURE_TYPE: Record<CodigoFuente, string> = {
  [CodigoFuente.SAN]: 'SANITARIO',
  [CodigoFuente.EDU]: 'EDUCATIVO',
  [CodigoFuente.CUL]: 'CULTURAL',
  [CodigoFuente.DEP]: 'DEPORTIVO',
  [CodigoFuente.EME]: 'EMERGENCIAS',
  [CodigoFuente.IND]: 'INDUSTRIAL',
  [CodigoFuente.TRA]: 'VIAL',
  [CodigoFuente.ENE]: 'ENERGIA',
  [CodigoFuente.HID]: 'HIDRAULICO',
  [CodigoFuente.ADM]: 'MUNICIPAL',
  [CodigoFuente.REL]: 'RELIGIOSO',
  [CodigoFuente.COM]: 'COMBUSTIBLE',
  [CodigoFuente.ACO]: 'SOCIAL',
  [CodigoFuente.SEG]: 'POLICIAL',
  [CodigoFuente.TEL]: 'TELECOM',
};

/**
 * Tipos de servicio de datos geoespaciales soportados
 */
export type ServiceType = 'WFS' | 'CKAN' | 'WMS' | 'REST' | 'SPARQL';

/**
 * Configuración de una fuente oficial de datos geoespaciales
 */
export interface OfficialSource {
  /** Identificador único de la fuente (ej: 'DERA_G12_SALUD') */
  id: string;
  
  /** Nombre descriptivo de la fuente */
  name: string;
  
  /** Tipologías PTEL que cubre esta fuente */
  tipologias: CodigoFuente[];
  
  /** Tipo de servicio (WFS, CKAN, REST, etc.) */
  serviceType: ServiceType;
  
  /** URL base del endpoint del servicio */
  endpoint: string;
  
  /** Capas disponibles (para WFS/WMS) */
  layers?: string[];
  
  /** ID del dataset (para CKAN) */
  datasetId?: string;
  
  /** Campo que contiene el ID único del registro */
  idField?: string;
  
  /** Campo que contiene el nombre de la infraestructura */
  nameField: string;
  
  /** Campo que contiene la geometría */
  geometryField: string;
  
  /** Sistema de coordenadas de referencia */
  crs: string;
  
  /** 
   * Nivel de autoridad de la fuente (0-100)
   * 100 = Fuente oficial primaria (ej: SAS para sanitarios)
   * 80 = Fuente oficial secundaria (ej: DERA)
   * 60 = Fuente de referencia (ej: OSM)
   */
  authority: number;
  
  /** Porcentaje de cobertura estimado (0-100) */
  coverage: number;
  
  /** Rango de precisión en metros [min, max] */
  precisionMeters: [number, number];
  
  /** Si requiere filtro por municipio para consultas eficientes */
  requiresMunicipalityFilter: boolean;
  
  /** Si la fuente está activa y disponible */
  active: boolean;
  
  /** Campo de municipio para filtros CQL */
  municipalityField?: string;
  
  /** Campo de provincia para filtros CQL */
  provinceField?: string;
  
  /** Notas adicionales sobre la fuente */
  notes?: string;
  
  /** Fecha de última verificación de disponibilidad */
  lastVerified?: string;
}

// =============================================================================
// ENDPOINTS BASE
// =============================================================================

/**
 * URLs base de servicios DERA (Datos Espaciales de Referencia de Andalucía)
 */
const DERA_BASE = 'https://www.ideandalucia.es/services';

/**
 * Endpoints verificados de fuentes oficiales
 */
export const ENDPOINTS = {
  // DERA - IECA
  DERA_G8: `${DERA_BASE}/DERA_g8_tejido_economico/wfs`,
  DERA_G9: `${DERA_BASE}/DERA_g9_transporte_comunicaciones/wfs`,
  DERA_G10: `${DERA_BASE}/DERA_g10_infraestructura_energetica/wfs`,
  DERA_G11: `${DERA_BASE}/DERA_g11_patrimonio/wfs`,
  DERA_G12: `${DERA_BASE}/DERA_g12_servicios/wfs`,
  DERA_G13: `${DERA_BASE}/DERA_g13_limites_administrativos/wfs`,
  
  // Servicios especializados IECA
  ISE: `${DERA_BASE}/ISE/wfs`,
  SIPOB: `${DERA_BASE}/SIPOB/wfs`,
  
  // IAPH - Patrimonio Histórico
  IAPH_LOCALIZADOR: 'https://www.iaph.es/ide/localizador/wfs',
  IAPH_INMATERIAL: 'https://www.iaph.es/ide/inmaterial/wfs',
  IAPH_PMU: 'https://www.iaph.es/ide/pmu/wfs',
  
  // IDE Cultura - Junta de Andalucía
  IDE_CULTURA: 'https://ws096.juntadeandalucia.es/geoserver/bica_public/wfs',
  
  // CKAN - Datos Abiertos Junta de Andalucía
  CKAN_BASE: 'https://www.juntadeandalucia.es/datosabiertos/portal/api/3/action/datastore_search',
  
  // REDIAM - Medio Ambiente
  REDIAM_HIDRAULICAS: 'https://www.juntadeandalucia.es/medioambiente/mapwms/REDIAM_WFS_SP_Infraestructuras_Hidraulicas',
  REDIAM_WFS: 'https://www.juntadeandalucia.es/medioambiente/mapwms/REDIAM_WFS',
  
  // Agencia Andaluza de la Energía
  AGENCIA_ENERGIA: 'https://www.agenciaandaluzadelaenergia.es/mapwms/wfs',
  
  // IAID - Instituto Andaluz del Deporte
  IAID: 'https://www.juntadeandalucia.es/turismoydeporte/iaid/wfs',
  
  // Gasolineras - MITECO
  GASOLINERAS: 'https://geoportalgasolineras.es/geoportal-instalaciones/rest/busquedaEstaciones',
} as const;

// =============================================================================
// REGISTRO DE FUENTES OFICIALES
// =============================================================================

/**
 * Registro central de fuentes oficiales para geocodificación PTEL
 * 
 * Cada entrada define una fuente de datos oficial española con:
 * - Endpoint y configuración de acceso
 * - Tipologías PTEL que cubre
 * - Nivel de autoridad y cobertura
 * - Campos de datos relevantes
 */
export const OFFICIAL_SOURCES: OfficialSource[] = [
  // =========================================================================
  // SANITARIO (SAN)
  // =========================================================================
  {
    id: 'DERA_G12_SALUD',
    name: 'DERA G12 - Servicios Sanitarios (SICESS/SAS)',
    tipologias: [CodigoFuente.SAN],
    serviceType: 'WFS',
    endpoint: ENDPOINTS.DERA_G12,
    layers: [
      'g12_01_CentroSalud',
      'g12_02_Hospital_CAE',
      // 'g12_03_Consultorio' retirada — no existe en WFS DERA G12 real (smoke 11-may);
      // los consultorios rurales del SAS están consolidados bajo g12_01_CentroSalud.
    ],
    idField: 'CODIGO_NICA',
    nameField: 'DENOMINACION',
    geometryField: 'geometry',
    crs: 'EPSG:25830',
    authority: 100,
    coverage: 100,
    precisionMeters: [2, 10],
    requiresMunicipalityFilter: true,
    active: true,
    municipalityField: 'MUNICIPIO',
    provinceField: 'PROVINCIA',
    notes: 'Fuente oficial del Servicio Andaluz de Salud. ~1,500 centros sanitarios.',
    lastVerified: '2025-01-05',
  },
  
  // =========================================================================
  // EDUCATIVO (EDU)
  // =========================================================================
  {
    id: 'CKAN_EDUCACION',
    name: 'API CKAN - Centros Educativos Andalucía',
    tipologias: [CodigoFuente.EDU],
    serviceType: 'CKAN',
    endpoint: ENDPOINTS.CKAN_BASE,
    datasetId: '15aabed2-eec3-4b99-a027-9af5e27c9cac',
    idField: 'codigo',
    nameField: 'denominacion_especifica',
    geometryField: 'latitud,longitud',
    crs: 'EPSG:4326',
    authority: 100,
    coverage: 100,
    precisionMeters: [5, 25],
    requiresMunicipalityFilter: false,
    active: true,
    municipalityField: 'municipio',
    provinceField: 'provincia',
    notes: 'Directorio oficial de centros docentes no universitarios. Curso 2023/2024. ~3,800 centros.',
    lastVerified: '2025-01-05',
  },
  
  // =========================================================================
  // CULTURAL (CUL)
  // =========================================================================
  {
    id: 'IAPH_LOCALIZADOR',
    name: 'IAPH - Localizador Cartográfico del Patrimonio',
    tipologias: [CodigoFuente.CUL],
    serviceType: 'WFS',
    endpoint: ENDPOINTS.IAPH_LOCALIZADOR,
    layers: ['patrimonio_inmueble'],
    idField: 'codigo_iaph',
    nameField: 'denominacion',
    geometryField: 'geometry',
    crs: 'EPSG:25830',
    authority: 95,
    coverage: 90,
    precisionMeters: [5, 50],
    requiresMunicipalityFilter: true,
    active: true,
    municipalityField: 'municipio',
    provinceField: 'provincia',
    notes: 'Instituto Andaluz del Patrimonio Histórico. ~5,887 sitios georreferenciados.',
    lastVerified: '2025-01-05',
  },
  {
    id: 'DERA_G11_PATRIMONIO',
    name: 'DERA G11 - Patrimonio Histórico',
    tipologias: [CodigoFuente.CUL],
    serviceType: 'WFS',
    endpoint: ENDPOINTS.DERA_G11,
    layers: [
      'g11_01_BienInmueble',
      'g11_02_ConjuntoHistorico',
    ],
    idField: 'CODIGO',
    nameField: 'DENOMINACION',
    geometryField: 'geometry',
    crs: 'EPSG:25830',
    authority: 90,
    coverage: 85,
    precisionMeters: [10, 50],
    requiresMunicipalityFilter: true,
    active: true,
    municipalityField: 'MUNICIPIO',
    provinceField: 'PROVINCIA',
    notes: 'Catálogo General del Patrimonio Histórico Andaluz via IECA.',
    lastVerified: '2025-01-05',
  },
  {
    id: 'IDE_CULTURA',
    name: 'IDE Cultura - CGPHA',
    tipologias: [CodigoFuente.CUL],
    serviceType: 'WFS',
    endpoint: ENDPOINTS.IDE_CULTURA,
    layers: ['bica_public:inmuebles_bic'],
    idField: 'codigo',
    nameField: 'denominacion',
    geometryField: 'geometry',
    crs: 'EPSG:25830',
    authority: 95,
    coverage: 80,
    precisionMeters: [2, 25],
    requiresMunicipalityFilter: true,
    active: true,
    notes: 'Catálogo General del Patrimonio Histórico Andaluz con delimitaciones BIC.',
    lastVerified: '2025-01-05',
  },
  
  // =========================================================================
  // DEPORTIVO (DEP)
  // =========================================================================
  {
    id: 'IAID_DEPORTES',
    name: 'IAID - Censo de Instalaciones Deportivas',
    tipologias: [CodigoFuente.DEP],
    serviceType: 'WFS',
    endpoint: ENDPOINTS.IAID,
    layers: ['instalaciones_deportivas'],
    idField: 'codigo',
    nameField: 'nombre',
    geometryField: 'geometry',
    crs: 'EPSG:25830',
    authority: 95,
    coverage: 95,
    precisionMeters: [5, 25],
    requiresMunicipalityFilter: true,
    active: true,
    municipalityField: 'municipio',
    provinceField: 'provincia',
    notes: 'Instituto Andaluz del Deporte. Censo de instalaciones deportivas públicas y privadas.',
    lastVerified: '2025-01-05',
  },
  
  // =========================================================================
  // EMERGENCIAS (EME)
  // =========================================================================
  {
    id: 'ISE_EMERGENCIAS',
    name: 'ISE - Infraestructura de Seguridad y Emergencias',
    tipologias: [CodigoFuente.EME],
    serviceType: 'WFS',
    endpoint: ENDPOINTS.ISE,
    layers: [
      'bomberos',
      'proteccion_civil',
      'emergencias_112',
    ],
    idField: 'codigo',
    nameField: 'denominacion',
    geometryField: 'geometry',
    crs: 'EPSG:25830',
    authority: 100,
    coverage: 90,
    precisionMeters: [2, 15],
    requiresMunicipalityFilter: true,
    active: true,
    municipalityField: 'municipio',
    provinceField: 'provincia',
    notes: 'Servicio de emergencias 112 Andalucía. 86 parques de bomberos.',
    lastVerified: '2025-01-05',
  },
  
  // =========================================================================
  // INDUSTRIAL (IND)
  // =========================================================================
  {
    id: 'DERA_G8_INDUSTRIAL',
    name: 'DERA G8 - Tejido Económico e Industrial',
    tipologias: [CodigoFuente.IND],
    serviceType: 'WFS',
    endpoint: ENDPOINTS.DERA_G8,
    layers: [
      'g8_01_PoligonoIndustrial',
      'g8_02_AreaLogistica',
      'g8_03_PuntoActividadEconomica',
    ],
    idField: 'CODIGO',
    nameField: 'DENOMINACION',
    geometryField: 'geometry',
    crs: 'EPSG:25830',
    authority: 90,
    coverage: 100,
    precisionMeters: [10, 50],
    requiresMunicipalityFilter: true,
    active: true,
    municipalityField: 'MUNICIPIO',
    provinceField: 'PROVINCIA',
    notes: 'Polígonos industriales y áreas económicas de Andalucía.',
    lastVerified: '2025-01-05',
  },
  
  // DERA_G9_TRANSPORTE source entry eliminada (WTEP-DEPRECATE 17-may-2026).
  // Endpoint DERA_g9_transporte_com HTTP 404 permanente desde 19-mar-2026
  // (commit 88d2cc432 RESIL-M2+S6 `knownDead`). ENDPOINTS.DERA_G9 preservado
  // porque DERA_G9_TELECOM (TorresComGeocoder TEL) sigue dependiendo de él.
  // Ver .ptel/AUDIT_WTEP_20260517.md.

  
  // =========================================================================
  // ENERGÍA (ENE)
  // =========================================================================
  {
    id: 'DERA_G10_ENERGIA',
    name: 'DERA G10 - Infraestructura Energética',
    tipologias: [CodigoFuente.ENE],
    serviceType: 'WFS',
    endpoint: ENDPOINTS.DERA_G10,
    layers: [
      'g10_01_Subestacion',
      'g10_02_CentralGeneracion',
      'g10_03_ParqueEolico',
      'g10_04_PlantaSolar',
    ],
    idField: 'CODIGO',
    nameField: 'DENOMINACION',
    geometryField: 'geometry',
    crs: 'EPSG:25830',
    authority: 95,
    coverage: 100,
    precisionMeters: [10, 50],
    requiresMunicipalityFilter: false,
    active: true,
    municipalityField: 'MUNICIPIO',
    provinceField: 'PROVINCIA',
    notes: 'Infraestructura energética: subestaciones, centrales, renovables.',
    lastVerified: '2025-01-05',
  },
  {
    id: 'AGENCIA_ENERGIA',
    name: 'Agencia Andaluza de la Energía - Infraestructuras',
    tipologias: [CodigoFuente.ENE],
    serviceType: 'WFS',
    endpoint: ENDPOINTS.AGENCIA_ENERGIA,
    layers: [
      'subestaciones',
      'lineas_alta_tension',
      'generacion_renovable',
    ],
    idField: 'id',
    nameField: 'nombre',
    geometryField: 'geometry',
    crs: 'EPSG:25830',
    authority: 100,
    coverage: 95,
    precisionMeters: [5, 25],
    requiresMunicipalityFilter: false,
    active: true,
    notes: 'Fuente oficial de la Agencia Andaluza de la Energía. 19 capas actualizadas.',
    lastVerified: '2025-01-05',
  },
  
  // =========================================================================
  // HIDRÁULICO (HID)
  // =========================================================================
  {
    id: 'REDIAM_HIDRAULICAS',
    name: 'REDIAM - Infraestructuras Hidráulicas',
    tipologias: [CodigoFuente.HID],
    serviceType: 'WFS',
    endpoint: ENDPOINTS.REDIAM_HIDRAULICAS,
    layers: [
      'EDAR',
      'embalses',
      'estaciones_bombeo',
      'captaciones',
    ],
    idField: 'codigo',
    nameField: 'nombre',
    geometryField: 'geometry',
    crs: 'EPSG:25830',
    authority: 100,
    coverage: 100,
    precisionMeters: [5, 50],
    requiresMunicipalityFilter: false,
    active: true,
    notes: 'Red de Información Ambiental de Andalucía. EDAR, embalses, depósitos. ~4,400 infraestructuras.',
    lastVerified: '2025-01-05',
  },
  
  // =========================================================================
  // ADMINISTRATIVO (ADM)
  // =========================================================================
  {
    id: 'DERA_G12_ADMIN',
    name: 'DERA G12 - Servicios Administrativos',
    tipologias: [CodigoFuente.ADM],
    serviceType: 'WFS',
    endpoint: ENDPOINTS.DERA_G12,
    layers: [
      'g12_04_Ayuntamiento',
      'g12_05_OficinaPublica',
      'g12_06_Juzgado',
    ],
    idField: 'CODIGO',
    nameField: 'DENOMINACION',
    geometryField: 'geometry',
    crs: 'EPSG:25830',
    authority: 95,
    coverage: 100,
    precisionMeters: [2, 15],
    requiresMunicipalityFilter: true,
    active: true,
    municipalityField: 'MUNICIPIO',
    provinceField: 'PROVINCIA',
    notes: 'Ayuntamientos, oficinas públicas, juzgados de Andalucía.',
    lastVerified: '2025-01-05',
  },
  {
    id: 'SIPOB_ENTIDADES',
    name: 'SIPOB - Sistema de Información de Población',
    tipologias: [CodigoFuente.ADM],
    serviceType: 'WFS',
    endpoint: ENDPOINTS.SIPOB,
    layers: ['entidades_locales'],
    idField: 'codigo_ine',
    nameField: 'nombre',
    geometryField: 'geometry',
    crs: 'EPSG:25830',
    authority: 100,
    coverage: 100,
    precisionMeters: [10, 100],
    requiresMunicipalityFilter: false,
    active: true,
    notes: 'Entidades locales menores y núcleos de población con código INE.',
    lastVerified: '2025-01-05',
  },
  
  // =========================================================================
  // SEGURIDAD (SEG)
  // =========================================================================
  {
    id: 'DERA_G12_SEGURIDAD',
    name: 'DERA G12 - Seguridad y Policía',
    tipologias: [CodigoFuente.SEG],
    serviceType: 'WFS',
    endpoint: ENDPOINTS.DERA_G12,
    layers: [
      'g12_07_Comisaria',
      'g12_08_CuartelGuardiaCivil',
      'g12_09_PoliciaLocal',
    ],
    idField: 'CODIGO',
    nameField: 'DENOMINACION',
    geometryField: 'geometry',
    crs: 'EPSG:25830',
    authority: 90,
    coverage: 85,
    precisionMeters: [5, 25],
    requiresMunicipalityFilter: true,
    active: true,
    municipalityField: 'MUNICIPIO',
    provinceField: 'PROVINCIA',
    notes: 'Comisarías, cuarteles de Guardia Civil y policía local.',
    lastVerified: '2025-01-05',
  },
  
  // =========================================================================
  // COMERCIAL (COM)
  // =========================================================================
  {
    id: 'GASOLINERAS_MITECO',
    name: 'Geoportal Gasolineras MITECO',
    tipologias: [CodigoFuente.COM],
    serviceType: 'REST',
    endpoint: ENDPOINTS.GASOLINERAS,
    idField: 'ideess',
    nameField: 'rotulo',
    geometryField: 'latitud,longitud_wgs84',
    crs: 'EPSG:4326',
    authority: 100,
    coverage: 100,
    precisionMeters: [5, 15],
    requiresMunicipalityFilter: false,
    active: true,
    notes: 'Registro oficial de estaciones de servicio. Actualización diaria de precios.',
    lastVerified: '2025-01-05',
  },
  
  // =========================================================================
  // ACOGIDA (ACO)
  // =========================================================================
  {
    id: 'DERA_G12_SOCIAL',
    name: 'DERA G12 - Servicios Sociales',
    tipologias: [CodigoFuente.ACO],
    serviceType: 'WFS',
    endpoint: ENDPOINTS.DERA_G12,
    layers: [
      'g12_10_ResidenciaMayores',
      'g12_11_CentroDia',
      'g12_12_CentroSocial',
    ],
    idField: 'CODIGO',
    nameField: 'DENOMINACION',
    geometryField: 'geometry',
    crs: 'EPSG:25830',
    authority: 90,
    coverage: 85,
    precisionMeters: [5, 25],
    requiresMunicipalityFilter: true,
    active: true,
    municipalityField: 'MUNICIPIO',
    provinceField: 'PROVINCIA',
    notes: 'Residencias de mayores, centros de día, centros sociales.',
    lastVerified: '2025-01-05',
  },
  
  // =========================================================================
  // RELIGIOSO (REL)
  // =========================================================================
  {
    id: 'IAPH_RELIGIOSO',
    name: 'IAPH - Patrimonio Religioso',
    tipologias: [CodigoFuente.REL],
    serviceType: 'WFS',
    endpoint: ENDPOINTS.IAPH_LOCALIZADOR,
    layers: ['patrimonio_religioso'],
    idField: 'codigo_iaph',
    nameField: 'denominacion',
    geometryField: 'geometry',
    crs: 'EPSG:25830',
    authority: 85,
    coverage: 70,
    precisionMeters: [10, 50],
    requiresMunicipalityFilter: true,
    active: true,
    municipalityField: 'municipio',
    provinceField: 'provincia',
    notes: 'Iglesias, ermitas y edificios religiosos catalogados.',
    lastVerified: '2025-01-05',
  },
  
  // =========================================================================
  // TELECOMUNICACIONES (TEL)
  // =========================================================================
  {
    id: 'DERA_G9_TELECOM',
    name: 'DERA G9 - Telecomunicaciones',
    tipologias: [CodigoFuente.TEL],
    serviceType: 'WFS',
    endpoint: ENDPOINTS.DERA_G9,
    layers: [
      'g9_05_TorreTelecom',
      'g9_06_Repetidor',
    ],
    idField: 'CODIGO',
    nameField: 'DENOMINACION',
    geometryField: 'geometry',
    crs: 'EPSG:25830',
    authority: 80,
    coverage: 60,
    precisionMeters: [10, 50],
    requiresMunicipalityFilter: true,
    active: true,
    municipalityField: 'MUNICIPIO',
    provinceField: 'PROVINCIA',
    notes: 'Torres de telecomunicaciones y repetidores. Cobertura parcial.',
    lastVerified: '2025-01-05',
  },
];

// =============================================================================
// FUNCIONES DE CONSULTA
// =============================================================================

/**
 * Obtiene la fuente oficial principal para una tipología PTEL
 * 
 * Retorna la fuente con mayor autoridad para la tipología especificada.
 * 
 * @param tipologia - Código de tipología PTEL (ej: 'SAN', 'EDU')
 * @returns Fuente oficial principal o null si no hay cobertura
 * 
 * @example
 * ```typescript
 * const source = getSourceForTipologia('SAN');
 * // { id: 'DERA_G12_SALUD', authority: 100, ... }
 * ```
 */
export function getSourceForTipologia(tipologia: CodigoFuente): OfficialSource | null {
  const sources = getSourcesForTipologia(tipologia);
  if (sources.length === 0) return null;
  
  // Retorna la fuente con mayor autoridad
  return sources.reduce((best, current) => 
    current.authority > best.authority ? current : best
  );
}

/**
 * Obtiene todas las fuentes oficiales para una tipología PTEL
 * 
 * Retorna un array de fuentes ordenadas por autoridad descendente.
 * Útil para implementar fallback entre fuentes.
 * 
 * @param tipologia - Código de tipología PTEL
 * @returns Array de fuentes oficiales (puede estar vacío)
 * 
 * @example
 * ```typescript
 * const sources = getSourcesForTipologia('CUL');
 * // [{ id: 'IAPH_LOCALIZADOR', authority: 95, ... }, 
 * //  { id: 'DERA_G11_PATRIMONIO', authority: 90, ... }]
 * ```
 */
export function getSourcesForTipologia(tipologia: CodigoFuente): OfficialSource[] {
  return OFFICIAL_SOURCES
    .filter(source => source.active && source.tipologias.includes(tipologia))
    .sort((a, b) => b.authority - a.authority);
}

/**
 * Obtiene el nivel de autoridad de una fuente por su ID
 * 
 * @param sourceId - Identificador único de la fuente
 * @returns Nivel de autoridad (0-100) o 0 si no existe
 * 
 * @example
 * ```typescript
 * getSourceAuthority('DERA_G12_SALUD'); // 100
 * getSourceAuthority('OSM_FALLBACK'); // 0 (no existe)
 * ```
 */
export function getSourceAuthority(sourceId: string): number {
  const source = OFFICIAL_SOURCES.find(s => s.id === sourceId);
  return source?.authority ?? 0;
}

/**
 * Verifica si una tipología tiene cobertura oficial
 * 
 * @param tipologia - Código de tipología PTEL
 * @returns true si hay al menos una fuente oficial activa
 * 
 * @example
 * ```typescript
 * hasOfficialCoverage('SAN'); // true
 * hasOfficialCoverage('XXX'); // false
 * ```
 */
export function hasOfficialCoverage(tipologia: CodigoFuente): boolean {
  return OFFICIAL_SOURCES.some(
    source => source.active && source.tipologias.includes(tipologia)
  );
}

/**
 * Obtiene todas las fuentes oficiales activas
 * 
 * @returns Array de todas las fuentes activas
 * 
 * @example
 * ```typescript
 * const activeSources = getActiveSources();
 * console.log(`${activeSources.length} fuentes activas`);
 * ```
 */
export function getActiveSources(): OfficialSource[] {
  return OFFICIAL_SOURCES.filter(source => source.active);
}

/**
 * Obtiene una fuente oficial por su ID
 * 
 * @param sourceId - Identificador único de la fuente
 * @returns Fuente oficial o undefined si no existe
 * 
 * @example
 * ```typescript
 * const source = getSourceById('DERA_G12_SALUD');
 * ```
 */
export function getSourceById(sourceId: string): OfficialSource | undefined {
  return OFFICIAL_SOURCES.find(s => s.id === sourceId);
}

/**
 * Obtiene todas las tipologías con cobertura oficial
 * 
 * @returns Array de códigos de tipología con fuentes activas
 */
export function getCoveredTipologias(): CodigoFuente[] {
  const covered = new Set<CodigoFuente>();
  
  for (const source of OFFICIAL_SOURCES) {
    if (source.active) {
      for (const tipologia of source.tipologias) {
        covered.add(tipologia);
      }
    }
  }
  
  return Array.from(covered);
}

/**
 * Obtiene estadísticas del registro de fuentes
 * 
 * @returns Objeto con estadísticas agregadas
 */
export function getRegistryStats(): {
  totalSources: number;
  activeSources: number;
  coveredTipologias: number;
  avgAuthority: number;
  avgCoverage: number;
} {
  const active = getActiveSources();
  const covered = getCoveredTipologias();
  
  const avgAuthority = active.length > 0
    ? active.reduce((sum, s) => sum + s.authority, 0) / active.length
    : 0;
    
  const avgCoverage = active.length > 0
    ? active.reduce((sum, s) => sum + s.coverage, 0) / active.length
    : 0;
  
  return {
    totalSources: OFFICIAL_SOURCES.length,
    activeSources: active.length,
    coveredTipologias: covered.length,
    avgAuthority: Math.round(avgAuthority),
    avgCoverage: Math.round(avgCoverage),
  };
}

/**
 * Obtiene fuentes por tipo de servicio
 * 
 * @param serviceType - Tipo de servicio (WFS, CKAN, REST, etc.)
 * @returns Array de fuentes del tipo especificado
 */
export function getSourcesByServiceType(serviceType: ServiceType): OfficialSource[] {
  return OFFICIAL_SOURCES.filter(
    source => source.active && source.serviceType === serviceType
  );
}

// =============================================================================
// ADAPTADOR TipologiaPTEL macro-19 → CodigoFuente (TUN3)
// =============================================================================

/**
 * Adaptador formal `TipologiaPTEL` (macro-19) → `CodigoFuente` (15 valores).
 *
 * Inversa de `TIPOLOGIA_TO_INFRASTRUCTURE_TYPE`. 4 cat macro hacen merge a
 * códigos compartidos:
 * - `BOMBEROS` y `EMERGENCIAS` → `EME`
 * - `VIAL` y `PUERTO` → `TRA`
 *
 * 2 cat macro carecen de fuente oficial dedicada y devuelven `null`:
 * - `RSU`: residuos sólidos urbanos (geocodificación vía CartoCiudad/OSM)
 * - `GENERICO`: fallback sin discriminador útil
 *
 * Mapeo autoritativo en `.ptel/DECISION_TIPOLOGIA_UNIFY_20260507_TUN1.md` §6.1.
 *
 * @param t - Categoría macro PTEL
 * @returns Código de fuente o `null` si no hay fuente oficial dedicada.
 *
 * @example
 * tipologiaACodigoFuente(TipologiaPTEL.SANITARIO);   // CodigoFuente.SAN
 * tipologiaACodigoFuente(TipologiaPTEL.BOMBEROS);    // CodigoFuente.EME (merge)
 * tipologiaACodigoFuente(TipologiaPTEL.RSU);         // null
 */
export function tipologiaACodigoFuente(t: TipologiaPTELMacro): CodigoFuente | null {
  switch (t) {
    case TipologiaPTELMacro.SANITARIO:    return CodigoFuente.SAN;
    case TipologiaPTELMacro.EDUCATIVO:    return CodigoFuente.EDU;
    case TipologiaPTELMacro.POLICIAL:     return CodigoFuente.SEG;
    case TipologiaPTELMacro.BOMBEROS:     return CodigoFuente.EME;  // merge
    case TipologiaPTELMacro.CULTURAL:     return CodigoFuente.CUL;
    case TipologiaPTELMacro.RELIGIOSO:    return CodigoFuente.REL;
    case TipologiaPTELMacro.DEPORTIVO:    return CodigoFuente.DEP;
    case TipologiaPTELMacro.MUNICIPAL:    return CodigoFuente.ADM;
    case TipologiaPTELMacro.SOCIAL:       return CodigoFuente.ACO;
    case TipologiaPTELMacro.COMBUSTIBLE:  return CodigoFuente.COM;
    case TipologiaPTELMacro.EMERGENCIAS:  return CodigoFuente.EME;
    case TipologiaPTELMacro.HIDRAULICO:   return CodigoFuente.HID;
    case TipologiaPTELMacro.ENERGIA:      return CodigoFuente.ENE;
    case TipologiaPTELMacro.TELECOM:      return CodigoFuente.TEL;
    case TipologiaPTELMacro.VIAL:         return CodigoFuente.TRA;
    case TipologiaPTELMacro.INDUSTRIAL:   return CodigoFuente.IND;
    case TipologiaPTELMacro.RSU:          return null;  // fallback CartoCiudad/OSM
    case TipologiaPTELMacro.PUERTO:       return CodigoFuente.TRA;  // merge
    case TipologiaPTELMacro.GENERICO:     return null;  // fallback
    default: {
      const _exhaustive: never = t;
      return _exhaustive;
    }
  }
}

// =============================================================================
// EXPORTACIONES
// =============================================================================

export default {
  OFFICIAL_SOURCES,
  ENDPOINTS,
  TIPOLOGIA_TO_INFRASTRUCTURE_TYPE,
  getSourceForTipologia,
  getSourcesForTipologia,
  getSourceAuthority,
  hasOfficialCoverage,
  getActiveSources,
  getSourceById,
  getCoveredTipologias,
  getRegistryStats,
  getSourcesByServiceType,
  tipologiaACodigoFuente,
};
